export const paths = {
  home: "/",
  login: "/login",
  presentation: "/presentation",
  cart: "/cart",
  checkout: "/checkout",
  payment: "/payment",
  search: "/search",
  brands: "/brands",
  new: "/new",
  all: "/all",
  promotions: "/promotions",
  bestSellers: "/best-sellers",
  freeShipping: "/free-shipping",
  campaign: "/campaign",
  departmentBeauty: "/departments/beauty",
  faq: "/faq",
  contact: "/contact",
  returnForm: "/return-form",
  wishlist: "/wishlist",
  account: "/account",
  accountOrder: (id: number) => `/account/orders/${id}`,
  paymentDelivery: "/payment-delivery",
  returnsPolicy: "/returns-policy",
  terms: "/terms",
  privacy: "/privacy",
  brand: (slug: string) => `/brand/${slug}`,
  unsubscribe: (hash: string) => `/unsubscribe/${hash}`,
  accountTab: (tab: AccountTabUrl) => (tab ? `${paths.account}/${tab}` : paths.account),
  searchQuery: (q: string) => `${paths.search}?s=${encodeURIComponent(q)}`
} as const;

export type AccountTabId = "" | "retur" | "informatii" | "retea" | "influencer" | "adrese" | "firme" | "notificari";
export type AccountTabUrl = "" | "returns" | "profile" | "network" | "influencer" | "addresses" | "companies" | "notifications";

const ACCOUNT_URL_TO_ID: Record<AccountTabUrl, AccountTabId> = {
  "": "",
  returns: "retur",
  profile: "informatii",
  network: "retea",
  influencer: "influencer",
  addresses: "adrese",
  companies: "firme",
  notifications: "notificari"
};

const ACCOUNT_ID_TO_URL: Record<AccountTabId, AccountTabUrl> = {
  "": "",
  retur: "returns",
  informatii: "profile",
  retea: "network",
  influencer: "influencer",
  adrese: "addresses",
  firme: "companies",
  notificari: "notifications"
};

export function accountTabFromUrl(tab: string): AccountTabId | null {
  if(tab in ACCOUNT_URL_TO_ID) return ACCOUNT_URL_TO_ID[tab as AccountTabUrl];
  return null;
}

export function accountTabToUrl(tab: AccountTabId): AccountTabUrl {
  return ACCOUNT_ID_TO_URL[tab];
}

export function accountTabPath(tab: AccountTabId): string {
  const url = accountTabToUrl(tab);
  return url ? `${paths.account}/${url}` : paths.account;
}

const LEGACY_EXACT: Record<string, string> = {
  "/autentificare": paths.login,
  "/cos": paths.cart,
  "/comanda": paths.checkout,
  "/plata": paths.payment,
  "/cautare": paths.search,
  "/branduri": paths.brands,
  "/noutati": paths.new,
  "/promotii": paths.promotions,
  "/transport-gratis": paths.freeShipping,
  "/campanie": paths.campaign,
  "/formular-returnare": paths.returnForm,
  "/cont": paths.account,
  "/despre-plata-si-livrare": paths.paymentDelivery,
  "/returnare-produse": paths.returnsPolicy,
  "/termeni-si-conditii": paths.terms,
  "/politica-de-confidentialitate": paths.privacy
};

const LEGACY_ACCOUNT_TAB: Record<string, AccountTabUrl> = {
  retur: "returns",
  informatii: "profile",
  retea: "network",
  adrese: "addresses",
  firme: "companies",
  notificari: "notifications"
};

/** Map old Romanian storefront paths to new English URLs (pathname only). */
export function resolveLegacyPath(pathname: string): string | null {
  const p = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  if(LEGACY_EXACT[p]) return LEGACY_EXACT[p];
  const cont = p.match(/^\/cont\/([^/?#]+)$/);
  if(cont) {
    const tab = LEGACY_ACCOUNT_TAB[cont[1]];
    if(tab) return paths.accountTab(tab);
    return paths.account;
  }
  const unsub = p.match(/^\/dezabonare\/([^/?#]+)$/);
  if(unsub) return paths.unsubscribe(unsub[1]);
  return null;
}

/** Normalize CMS / DB hrefs (keeps query string). */
export function normalizeShopHref(href: string): string {
  if(!href || href === "#") return href;
  if(/^https?:\/\//i.test(href) || href.startsWith("mailto:")) return href;
  const q = href.indexOf("?");
  const pathname = q >= 0 ? href.slice(0, q) : href;
  const search = q >= 0 ? href.slice(q) : "";
  const mapped = resolveLegacyPath(pathname);
  return mapped ? `${mapped}${search}` : href;
}

import type {ShopSiteStatus} from "@corporatesclub/shared";

export const PUBLIC_PATHS = [paths.login, paths.presentation, "/vadmin/login"] as const;

export const AUTH_REQUIRED_PATHS = [
  paths.account
] as const;

const GUEST_ORDER_PATHS = [paths.cart, paths.checkout, paths.payment] as const;

export function pathRequiresAuth(pathname: string, status: Pick<ShopSiteStatus, "publicAccess" | "guestOrdersAllowed">): boolean {
  if(PUBLIC_PATHS.includes(pathname as typeof PUBLIC_PATHS[number])) return false;
  if(!status.publicAccess) return true;
  if(AUTH_REQUIRED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return true;
  if(!status.guestOrdersAllowed && GUEST_ORDER_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return true;
  return false;
}

export function product(slug: string): string {
  return `/p/${slug}`;
}
